/**
 * Form Validator
 *
 * Client-side validation helpers for required fields and translated messages.
 */

import type { ZodIssue, ZodTypeAny } from "zod";
import { notifier } from "../core/notifier/notifier";
import { FormValidatorListener } from "./form-validator-listener";
import { translator } from "../translation/translator";
import { phpDict } from "../core/php-data-access";
import { getEventScope } from "../core/event-scope/event-scope";
import { Roles } from "../constants/roles";
import { FormBase } from "./form-base-form";

type LanguageCode = "th" | "en" | "ja";
type TranslationValues = Record<string, string>;
type FormFieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

interface FormValidationDictionary {
    field_names?: Record<string, TranslationValues>;
    [key: string]: TranslationValues | Record<string, TranslationValues> | undefined;
}

interface FieldValidationResult {
    isValid: boolean;
    message: string;
}

type SchemaValidationIssue = ZodIssue & {
    unionErrors?: Array<{
        issues: ZodIssue[];
    }>;
};

type ValidationSchema = ZodTypeAny;

interface InvalidFieldEntry {
    field: HTMLElement;
    message: string;
}

interface ValidationResult {
    isValid: boolean;
    invalidFields: InvalidFieldEntry[];
    firstError: string;
}

interface ValidateFormOptions {
    focusFirstInvalid?: boolean;
    schema?: ValidationSchema;
    data?: unknown;
}

class FormValidatorDom extends FormBase {
    public getRequiredFields(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, '[data-validation="required"]', HTMLElement);
    }

    public getLabelByFor(root: ParentNode, fieldId: string): HTMLLabelElement | null {
        return this.queryElement(root, `label[for="${CSS.escape(fieldId)}"]`, HTMLLabelElement);
    }

    public getClosestLabel(root: Event | EventTarget | Element | null | undefined): HTMLLabelElement | null {
        return this.closest(root, "label", HTMLLabelElement);
    }

    public getReviewQuestion(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, ".review-question", HTMLElement);
    }

    public getReviewQuestionLabel(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".review-question-label", HTMLElement);
    }

    public getReviewQuestionFieldRoot(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, ".review-question", HTMLElement);
    }

    public getReviewQuestionFocusTarget(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            '[data-role="rating-star"], textarea, select, input:not([type="hidden"]), button, [tabindex]',
            HTMLElement,
        );
    }

    public getWysiwygEditor(textarea: HTMLTextAreaElement): HTMLElement | null {
        const previousSibling = textarea.previousElementSibling;
        if (previousSibling instanceof HTMLElement && previousSibling.dataset.role === Roles.WYSIWYG_EDITOR) {
            return previousSibling;
        }

        return this.queryElement(textarea.parentElement, `[data-role="${Roles.WYSIWYG_EDITOR}"]`, HTMLElement);
    }

    public getWysiwygFocusTarget(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.WYSIWYG_CONTENT}"]`, HTMLElement);
    }

    public hasMeaningfulWysiwygEmbed(root: ParentNode): boolean {
        return (
            this.queryElement(root, "img, video, iframe, audio, source, .wysiwyg-upload-box", HTMLElement) instanceof
            HTMLElement
        );
    }

    public getValidationFields(root: ParentNode): HTMLElement[] {
        return this.queryElements(
            root,
            '[data-validation="required"], input[type="email"], input[type="url"], input[minlength], input[maxlength], textarea[minlength], textarea[maxlength]',
            HTMLElement,
        );
    }

    public getRealtimeFields(root: ParentNode): FormFieldElement[] {
        return this.queryElements(root, "input, textarea, select", HTMLElement).filter((field) =>
            isFormFieldElement(field),
        );
    }

    public getForms(root: ParentNode): HTMLFormElement[] {
        return this.queryElements(root, "form", HTMLFormElement);
    }

    public getFieldByName(form: HTMLFormElement, fieldName: string): HTMLElement | null {
        return this.queryElement(form, `[name="${CSS.escape(fieldName)}"]`, HTMLElement);
    }

    public getFieldContainer(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, ".form-field, .review-question", HTMLElement);
    }

    public getFieldLabelFromContainer(root: Event | EventTarget | Element | null | undefined): HTMLLabelElement | null {
        const container = this.getFieldContainer(root);
        if (!container) return null;

        return this.queryElement(container, ".form-label", HTMLLabelElement);
    }

    public getFormBySelector(selector: string): HTMLFormElement | null {
        return this.queryElement(document, selector, HTMLFormElement);
    }
}

const formValidatorDom = new FormValidatorDom();

interface AddFieldErrorOptions {
    focus?: boolean;
}

interface AttachValidationOptions {
    showNotification?: boolean;
    preventSubmit?: boolean;
    realTimeValidation?: boolean;
    onValidationFailed?: (validation: ValidationResult) => void;
    onValidationSuccess?: (validation: ValidationResult) => void;
}

interface ValidateOptions {
    showNotification?: boolean;
    schema?: ValidationSchema;
    data?: unknown;
}

function getFormValidationDict(): FormValidationDictionary | null {
    return phpDict("FormValidation") as unknown as FormValidationDictionary | null;
}

function isTranslationValues(value: FormValidationDictionary[string] | undefined): value is TranslationValues {
    return (
        !!value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        Object.values(value).every((item) => typeof item === "string")
    );
}

function isFormFieldElement(field: Element): field is FormFieldElement {
    return (
        field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement
    );
}

function getFieldValue(field: HTMLElement): string {
    if (field instanceof HTMLTextAreaElement && field.getAttribute("data-wysiwyg") === "true") {
        const rawValue = field.value.trim();
        if (!rawValue) {
            return "";
        }

        const wrapper = document.createElement("div");
        wrapper.innerHTML = rawValue;

        const hasMeaningfulEmbed = formValidatorDom.hasMeaningfulWysiwygEmbed(wrapper);
        const text = String(wrapper.textContent || "")
            .replace(/\u00a0/g, " ")
            .trim();

        return hasMeaningfulEmbed || text !== "" ? rawValue : "";
    }

    return isFormFieldElement(field) ? field.value.trim() : "";
}

export class FormValidator {
    public constructor() {}

    public getCurrentLanguage(): LanguageCode {
        return translator?.currentLanguage || "th";
    }

    public getMessage(key: string, replacements: TranslationValues = {}): string {
        const lang = this.getCurrentLanguage();
        const translations = getFormValidationDict() || {};
        const translation = translations[key];

        if (!isTranslationValues(translation)) {
            return `Missing translation: ${key}`;
        }

        let message = translation[lang] || translation.th || `Missing ${lang} translation for ${key}`;

        Object.keys(replacements).forEach((replaceKey) => {
            message = message.replace(`{${replaceKey}}`, replacements[replaceKey]);
        });

        return message;
    }

    public getFieldDisplayName(field: HTMLElement): string {
        const lang = this.getCurrentLanguage();
        let label: Element | null = null;

        if (field.id) {
            label = formValidatorDom.getLabelByFor(document, field.id);
        }

        if (!label) {
            label = formValidatorDom.getClosestLabel(field);
        }

        if (!label) {
            label = formValidatorDom.getFieldLabelFromContainer(field);
        }

        if (!label || !(label.textContent || "").trim()) {
            const reviewQuestion = formValidatorDom.getReviewQuestion(field);
            if (reviewQuestion) {
                const questionLabel = formValidatorDom.getReviewQuestionLabel(reviewQuestion);
                if (questionLabel) {
                    label = questionLabel;
                }
            }
        }

        const labelDict = getFormValidationDict();
        if (label && label.hasAttribute("data-translate")) {
            const translateKey = label.getAttribute("data-translate");
            const fieldNameTranslations = translateKey ? labelDict?.field_names?.[translateKey] : undefined;
            if (fieldNameTranslations && fieldNameTranslations[lang]) {
                return fieldNameTranslations[lang];
            }
        }

        if (label) {
            const langKey = `data-text-${lang}`;
            if (label.hasAttribute(langKey)) {
                const labelText = label.getAttribute(langKey);
                if (labelText) {
                    return labelText;
                }
            }
        }

        if (field.hasAttribute("data-translate")) {
            const translateKey = field.getAttribute("data-translate");
            const fieldNameTranslations = translateKey ? labelDict?.field_names?.[translateKey] : undefined;
            if (fieldNameTranslations && fieldNameTranslations[lang]) {
                return fieldNameTranslations[lang];
            }
        }

        const fieldName = isFormFieldElement(field) ? field.name : "";
        if (fieldName && labelDict?.field_names?.[fieldName]?.[lang]) {
            return labelDict.field_names[fieldName][lang];
        }

        if (label) {
            const labelText = (label.textContent || "").replace(/\s*\*\s*$/, "").trim();
            if (labelText) {
                return labelText;
            }
        }

        const defaultFieldName = labelDict?.field_names?.name;
        if (defaultFieldName && defaultFieldName[lang]) {
            return defaultFieldName[lang];
        }

        return fieldName || "field";
    }

    public getSchemaIssueFieldName(issue: SchemaValidationIssue): string {
        const directField = issue.path.find((segment) => typeof segment === "string");
        if (typeof directField === "string" && directField) {
            return directField;
        }

        const nestedIssues = Array.isArray(issue.unionErrors)
            ? issue.unionErrors.flatMap((entry) => (Array.isArray(entry?.issues) ? entry.issues : []))
            : [];

        for (const nestedIssue of nestedIssues) {
            const nestedField = this.getSchemaIssueFieldName(nestedIssue);
            if (nestedField) {
                return nestedField;
            }
        }

        return "";
    }

    public validateField(field: HTMLElement): FieldValidationResult {
        const fieldName = this.getFieldDisplayName(field);
        const value = getFieldValue(field);

        if (field.getAttribute("data-validation") === "required" && !value) {
            return {
                isValid: false,
                message: this.getMessage("required_field_name", { fieldName }),
            };
        }

        if (field instanceof HTMLInputElement && field.type === "email" && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                return {
                    isValid: false,
                    message: this.getMessage("invalid_email"),
                };
            }
        }

        if (field instanceof HTMLInputElement && field.type === "url" && value) {
            try {
                new URL(value);
            } catch {
                return {
                    isValid: false,
                    message: this.getMessage("invalid_url"),
                };
            }
        }

        if (field.hasAttribute("minlength") && value) {
            const minLengthValue = field.getAttribute("minlength");
            const minLength = minLengthValue ? parseInt(minLengthValue, 10) : NaN;
            if (!Number.isNaN(minLength) && value.length < minLength) {
                return {
                    isValid: false,
                    message: this.getMessage("min_length", { min: String(minLength) }),
                };
            }
        }

        if (field.hasAttribute("maxlength") && value) {
            const maxLengthValue = field.getAttribute("maxlength");
            const maxLength = maxLengthValue ? parseInt(maxLengthValue, 10) : NaN;
            if (!Number.isNaN(maxLength) && value.length > maxLength) {
                return {
                    isValid: false,
                    message: this.getMessage("max_length", { max: String(maxLength) }),
                };
            }
        }

        return { isValid: true, message: "" };
    }

    public getSchemaIssueMessage(field: HTMLElement | null, issue: SchemaValidationIssue): string {
        const fieldName = field ? this.getFieldDisplayName(field) : "";

        if (issue.code === "too_small" && typeof issue.minimum === "number") {
            if (fieldName) {
                return this.getMessage("min_length_field", {
                    fieldName,
                    min: String(issue.minimum),
                });
            }

            return this.getMessage("min_length", { min: String(issue.minimum) });
        }

        if (issue.code === "too_big" && typeof issue.maximum === "number") {
            if (fieldName) {
                return this.getMessage("max_length_field", {
                    fieldName,
                    max: String(issue.maximum),
                });
            }

            return this.getMessage("max_length", { max: String(issue.maximum) });
        }

        if (issue.code === "invalid_string") {
            if (issue.validation === "email") {
                return this.getMessage("invalid_email");
            }

            if (issue.validation === "url") {
                return this.getMessage("invalid_url");
            }

            return this.getMessage("invalid_format");
        }

        if (issue.code === "invalid_type" && (issue.received === "undefined" || issue.received === "null")) {
            if (fieldName) {
                return this.getMessage("required_field_name", { fieldName });
            }

            return this.getMessage("required_field");
        }

        if (issue.code === "custom" || issue.code === "invalid_enum_value" || issue.code === "invalid_literal") {
            if (fieldName) {
                return this.getMessage("invalid_format_field", { fieldName });
            }

            return this.getMessage("invalid_format");
        }

        if (issue.code === "invalid_union" || issue.code === "invalid_intersection_types") {
            if (fieldName) {
                return this.getMessage("invalid_format_field", { fieldName });
            }

            return this.getMessage("invalid_format");
        }

        if (issue.code === "not_finite") {
            if (fieldName) {
                return this.getMessage("invalid_number_field", { fieldName });
            }

            return this.getMessage("invalid_number");
        }

        return this.getMessage("validation_failed");
    }

    public getFieldErrorTarget(field: HTMLElement): HTMLElement {
        if (field instanceof HTMLTextAreaElement && field.getAttribute("data-wysiwyg") === "true") {
            const wysiwygEditor = formValidatorDom.getWysiwygEditor(field);
            if (wysiwygEditor instanceof HTMLElement) {
                return wysiwygEditor;
            }
        }

        if (field instanceof HTMLInputElement && field.type === "hidden") {
            const reviewQuestion = formValidatorDom.getReviewQuestionFieldRoot(field);
            if (reviewQuestion instanceof HTMLElement) {
                return reviewQuestion;
            }
        }

        return field;
    }

    public hasFieldError(field: HTMLElement): boolean {
        return this.getFieldErrorTarget(field).classList.contains("is-invalid");
    }

    public focusField(field: HTMLElement): void {
        if (field instanceof HTMLTextAreaElement && field.getAttribute("data-wysiwyg") === "true") {
            const wysiwygEditor = formValidatorDom.getWysiwygEditor(field);
            if (wysiwygEditor instanceof HTMLElement) {
                const focusTarget = formValidatorDom.getWysiwygFocusTarget(wysiwygEditor);
                if (focusTarget instanceof HTMLElement) {
                    focusTarget.focus();
                    return;
                }
            }
        }

        if (field instanceof HTMLInputElement && field.type === "hidden") {
            const reviewQuestion = formValidatorDom.getReviewQuestionFieldRoot(field);
            if (reviewQuestion instanceof HTMLElement) {
                const focusTarget = formValidatorDom.getReviewQuestionFocusTarget(reviewQuestion);
                if (focusTarget instanceof HTMLElement) {
                    focusTarget.focus();
                    return;
                }
            }
        }

        field.focus();
    }

    public validateForm(form: HTMLFormElement, options: ValidateFormOptions = {}): ValidationResult {
        const invalidFields: InvalidFieldEntry[] = [];
        let firstErrorMessage = "";
        const { focusFirstInvalid = false, schema, data } = options;
        const invalidFieldNames = new Set<string>();

        const fields = formValidatorDom.getValidationFields(form);

        if (schema) {
            formValidatorDom.getRealtimeFields(form).forEach((field) => {
                this.removeFieldError(field);
            });
        }

        fields.forEach((field) => {
            if (!(field instanceof HTMLElement)) {
                return;
            }

            const result = this.validateField(field);
            if (!result.isValid) {
                const fieldName = isFormFieldElement(field) ? field.name : "";
                invalidFields.push({
                    field,
                    message: result.message,
                });

                if (fieldName) {
                    invalidFieldNames.add(fieldName);
                }

                if (!firstErrorMessage) {
                    firstErrorMessage = result.message;
                }

                this.addFieldError(field, { focus: false });
            } else {
                this.removeFieldError(field);
            }
        });

        if (schema) {
            const schemaResult = schema.safeParse(data);
            if (!schemaResult.success) {
                (schemaResult.error?.issues || []).forEach((issue) => {
                    const fieldName = this.getSchemaIssueFieldName(issue);
                    if (typeof fieldName !== "string") {
                        const issueMessage = this.getSchemaIssueMessage(null, issue);
                        if (!firstErrorMessage && issueMessage) {
                            firstErrorMessage = issueMessage;
                        }
                        return;
                    }

                    const field = formValidatorDom.getFieldByName(form, fieldName);
                    const issueMessage = this.getSchemaIssueMessage(field, issue);

                    if (invalidFieldNames.has(fieldName)) {
                        if (!firstErrorMessage && issueMessage) {
                            firstErrorMessage = issueMessage;
                        }
                        return;
                    }

                    if (field instanceof HTMLElement) {
                        invalidFields.push({
                            field,
                            message: issueMessage,
                        });
                        invalidFieldNames.add(fieldName);
                        this.addFieldError(field, { focus: false });
                    }

                    if (!firstErrorMessage && issueMessage) {
                        firstErrorMessage = issueMessage;
                    }
                });
            }
        }

        if (focusFirstInvalid && invalidFields.length > 0) {
            const firstInvalidField = invalidFields[0]?.field;
            if (firstInvalidField instanceof HTMLElement) {
                this.focusField(firstInvalidField);
            }
        }

        return {
            isValid: invalidFields.length === 0,
            invalidFields,
            firstError: firstErrorMessage,
        };
    }

    public addFieldError(field: HTMLElement, options: AddFieldErrorOptions = {}): void {
        const errorTarget = this.getFieldErrorTarget(field);
        errorTarget.classList.add("is-invalid");

        const { focus = false } = options;
        if (focus) {
            this.focusField(field);
        }
    }

    public removeFieldError(field: HTMLElement): void {
        this.getFieldErrorTarget(field).classList.remove("is-invalid");
    }

    public showValidationError(message: string): void {
        notifier.error(message, { duration: 5000 });
    }

    public attachToForm(form: HTMLFormElement, options: AttachValidationOptions = {}): void {
        const { showNotification = true, preventSubmit = true } = options;
        this.markRequiredLabels(form);

        FormValidatorListener.bindFormSubmit(form, (event) => {
            const validation = this.validateForm(form, { focusFirstInvalid: true });

            if (!validation.isValid) {
                if (preventSubmit) {
                    event.preventDefault();
                }

                if (showNotification && validation.firstError) {
                    this.showValidationError(validation.firstError);
                }

                if (options.onValidationFailed) {
                    options.onValidationFailed(validation);
                }

                return false;
            }

            if (options.onValidationSuccess) {
                options.onValidationSuccess(validation);
            }

            return true;
        });

        if (options.realTimeValidation !== false) {
            const fields = formValidatorDom.getRealtimeFields(form);
            fields.forEach((field) => {
                FormValidatorListener.bindFieldBlur(field, () => {
                    const result = this.validateField(field);
                    if (!result.isValid) {
                        this.addFieldError(field, { focus: false });
                    } else {
                        this.removeFieldError(field);
                    }
                });

                FormValidatorListener.bindFieldInput(field, () => {
                    if (this.hasFieldError(field)) {
                        const result = this.validateField(field);
                        if (result.isValid) {
                            this.removeFieldError(field);
                        }
                    }
                });
            });
        }
    }

    public markRequiredLabels(form: HTMLFormElement): void {
        const requiredFields = formValidatorDom.getRequiredFields(form);

        requiredFields.forEach((field) => {
            if (!(field instanceof HTMLElement)) {
                return;
            }

            if (field instanceof HTMLInputElement && field.type === "hidden") {
                return;
            }

            let label: Element | null = null;
            if (field.id) {
                label = formValidatorDom.getLabelByFor(document, field.id);
            }

            if (!label) {
                const fieldLabel = formValidatorDom.getFieldLabelFromContainer(field);
                if (fieldLabel instanceof HTMLLabelElement) {
                    label = fieldLabel;
                }
            }

            if (!label) {
                const closest = formValidatorDom.getClosestLabel(field);
                if (closest instanceof HTMLLabelElement) {
                    label = closest;
                }
            }

            if (label instanceof HTMLElement) {
                label.classList.add("required");
            }
        });
    }

    public init(options: AttachValidationOptions = {}): void {
        const forms = formValidatorDom.getForms(document);

        forms.forEach((form) => {
            if (!(form instanceof HTMLFormElement)) {
                return;
            }

            if (form.matches(`form[data-role="${Roles.POST_CREATE_FORM}"]`)) {
                return;
            }

            if (form.matches(`form[data-role="${Roles.MASTER_DATA_FORM}"]`)) {
                return;
            }

            if (form.matches(`form[data-role="${Roles.POINT_HISTORY_ADJUST_FORM}"]`)) {
                return;
            }

            if (form.hasAttribute("data-validation-attached")) {
                return;
            }

            this.attachToForm(form, options);
            form.setAttribute("data-validation-attached", "true");
        });
    }

    public validate(formSelector: string | HTMLFormElement, options: ValidateOptions = {}): ValidationResult {
        const form = typeof formSelector === "string" ? formValidatorDom.getFormBySelector(formSelector) : formSelector;

        if (!(form instanceof HTMLFormElement)) {
            return { isValid: false, invalidFields: [], firstError: "Form not found" };
        }

        const validation = this.validateForm(form, {
            focusFirstInvalid: true,
            schema: options.schema,
            data: options.data,
        });
        if (!validation.isValid && options.showNotification !== false) {
            this.showValidationError(validation.firstError);
        }

        return validation;
    }
}

const formValidatorInstance = new FormValidator();
export { formValidatorInstance as formValidator };

function initFormValidator(): void {
    const scope = getEventScope(document, "form-validator");

    const start = (): void => {
        const tryInit = (): void => {
            if (translator?.initialized) {
                formValidatorInstance.init({
                    showNotification: true,
                    preventSubmit: true,
                    realTimeValidation: true,
                });
            } else {
                setTimeout(tryInit, 100);
            }
        };

        setTimeout(tryInit, 200);
    };

    scope.once("init", () => {
        if (document.readyState === "loading") {
            scope.on("DOMContentLoaded", start, { once: true });
        } else {
            start();
        }
    });
}

export { initFormValidator };
